import Image from "next/image";
import { useCallback, useState } from "react";
import { Chain } from "viem";
import {
  Button,
  Flex,
  Text,
  Box,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";

// react-icons
import { FiMenu } from "react-icons/fi";
import { AiOutlineMore } from "react-icons/ai";
import { useDispatch } from "react-redux";
import { useAccount, useSwitchChain, useWalletClient } from "wagmi";

import { updateModalData } from "store";

import { MODAL_TYPES } from "@constants/modal";
import { CONNECT_WALLET } from "@constants/analytics";

import { chainIconMapping, trackEvent } from "utils";

import WalletInfo from "../WalletInfo";
import SubHeader from "./SubHeader";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { FaChevronDown } from "react-icons/fa6";
import { useRouter } from "next/router";
import useDappConfig from "@hooks/useDappConfig";
import useLedgerLive from "@hooks/useLedgerLive";

const Header = ({
  hideWalletButton,
  hideChainDropdown,
}: {
  hideWalletButton?: boolean;
  hideChainDropdown?: boolean;
}) => {
  const router = useRouter();
  const {
    isConnected,
    address,
    connector: activeConnector,
    chain,
  } = useAccount();
  const { data: signer } = useWalletClient();
  const { openConnectModal } = useConnectModal();
  const { restakeEnabledChains, claimEnabledChains, swapEnabledChains } =
    useDappConfig();
  const { switchChain } = useSwitchChain();
  const { isLedgerConnected } = useLedgerLive();

  const pathname = router.pathname;

  const chainList = pathname.includes("restake")
    ? restakeEnabledChains
    : pathname.includes("wrap")
    ? swapEnabledChains
    : claimEnabledChains;

  const chainListIds = chainList.map((chain) => chain.id);

  const handleSwitchChain = (chain: Chain) => {
    switchChain(
      { chainId: chain.id },
      {
        onError: (error) => {
          console.log(error.message);

          if (error.message.includes("Chain not configured")) {
            signer
              ?.addChain({ chain })
              .then(() => switchChain({ chainId: chain.id }));
          }
          return;
        },
      }
    );
  };

  const dispatch = useDispatch();
  const [isSubMenuVisible, setIsSubMenuVisible] = useState(false);

  const handleConnectWallet = useCallback(() => {
    // emitReduxEvent(EVENTS.OPEN_WALLET_MODAL);
    openConnectModal?.();
    trackEvent(CONNECT_WALLET, {
      type: "top_header",
    });
  }, []);

  const handleWalletClick = useCallback(() => {
    dispatch(
      updateModalData({
        modalType: MODAL_TYPES.WALLET_MODAL,
      })
    );
  }, [dispatch]);

  const handleSidebarMenuClick = useCallback(() => {
    dispatch(
      updateModalData({
        modalType: MODAL_TYPES.MOBILE_SIDEBAR_MENU_MODAL,
      })
    );
  }, [dispatch]);

  return (
    <Flex
      gap="16px"
      alignItems="center"
      justifyContent={{ base: "space-between", xl: "flex-end" }}
      position={{ xl: "fixed" }}
      right="0px"
      top="0"
      zIndex="1"
      p={{ base: "1rem", xl: 0 }}
      pt={{ xl: "2rem" }}
      pb={{ xl: 2 }}
      backgroundColor="backgroundCanvas"
      width={{ xl: "calc(100% - 240px)" }}
    >
      <Box display={{ base: "inherit", xl: "none" }} alignItems="center">
        <IconButton
          display={{ base: "flex", xl: "none" }}
          onClick={() => handleSidebarMenuClick()}
          aria-label="open menu"
          h="24px"
          w="24x"
          bg="transperent"
          color="textPrimary"
          variant="unstyled"
          icon={<FiMenu height="24px" width="24px" />}
        />
        <Box pl="1rem">
          <Image
            src="/assets/header/kelp-icon.svg"
            alt="kelp"
            height={24}
            width={19}
            loading="eager"
          />
        </Box>
      </Box>

      {!hideWalletButton && (
        <Flex>
          {!isConnected && !activeConnector ? (
            <Button
              borderRadius={{ base: "6px", lg: "8px" }}
              p={{ base: "4px 45px", md: "16px 64px" }}
              bg="backgroundCard"
              h={{ base: "32px", lg: "56px" }}
              _hover={{
                bg: "backgroundCard",
              }}
              w={{ base: "120px", lg: "240px" }}
              onClick={handleConnectWallet}
            >
              <Text
                fontWeight={{ base: "700", lg: "600" }}
                fontSize={{ base: "10px", lg: "14px" }}
                lineHeight={{ base: "16px", lg: "24px" }}
                color="textPrimary"
              >
                Connect wallet
              </Text>
            </Button>
          ) : (
            <Flex alignItems="center" gap={4}>
              {!hideChainDropdown && !isLedgerConnected && (
                <Box pos="relative">
                  <Menu>
                    <MenuButton
                      as={Button}
                      h={{ base: "32px", lg: "56px" }}
                      rightIcon={
                        chainListIds.length > 1 || !chain ? (
                          <FaChevronDown />
                        ) : (
                          <></>
                        )
                      }
                      w="100%"
                      textAlign="left"
                      className="menuButtonStake"
                      p={{ base: "16px", md: "16px 16px 16px 24px" }}
                      _active={{
                        bg: "backgroundCard",
                      }}
                      _hover={{
                        bg: "backgroundCard",
                      }}
                      borderRadius="8px"
                      bg="backgroundCard"
                      color="textPrimary"
                      fontWeight="400"
                      fontSize={{ base: "12px", md: "14px" }}
                    >
                      <Flex alignItems="center" gap="8px">
                        {chain && chainListIds.includes(chain.id) ? (
                          <>
                            <Box
                              overflow="hidden"
                              pos="relative"
                              borderRadius="50%"
                            >
                              <Image
                                height={24}
                                width={24}
                                src={(chain as any).iconUrl}
                                alt={chain!.name}
                              />
                            </Box>
                            <Flex
                              alignItems="center"
                              justify="space-between"
                              flex={1}
                              gap={2}
                              display={{ base: "none", lg: "block" }}
                            >
                              <Box flex={1}>{chain.name}</Box>
                            </Flex>
                          </>
                        ) : (
                          <Flex
                            alignItems="center"
                            justify="space-between"
                            flex={1}
                            gap={2}
                          >
                            <Box flex={1}>Unsupported Chain</Box>
                          </Flex>
                        )}
                      </Flex>
                    </MenuButton>
                    <MenuList
                      transformOrigin="top !important"
                      w={{
                        base: "180px",
                        sm: "100%",
                      }}
                      p="8px"
                      border="1px solid #AEDFCA"
                      bg="backgroundCard"
                      fontSize="14px"
                      fontWeight="400"
                    >
                      {chainList.map((option) => {
                        return (
                          <MenuItem
                            key={option.id}
                            padding={2}
                            marginBottom={1}
                            onClick={() => {
                              handleSwitchChain(option);
                            }}
                          >
                            <Flex
                              justifyContent="space-between"
                              w="100%"
                              alignItems="center"
                            >
                              <Flex alignItems="center" gap="8px" flex={1}>
                                <Box
                                  overflow="hidden"
                                  pos="relative"
                                  borderRadius="50%"
                                >
                                  <Image
                                    height={24}
                                    width={24}
                                    src={chainIconMapping[option.id]}
                                    alt={option!.name}
                                  />
                                </Box>
                                <Flex
                                  justify="space-between"
                                  flex={1}
                                  alignItems="center"
                                  gap={2}
                                >
                                  <Box flex={1} fontSize={14}>
                                    {option.name}
                                  </Box>
                                </Flex>
                              </Flex>
                            </Flex>
                          </MenuItem>
                        );
                      })}
                    </MenuList>
                  </Menu>
                </Box>
              )}
              <WalletInfo
                walletAddress={address}
                activeConnector={activeConnector!}
                onClick={handleWalletClick}
              />
            </Flex>
          )}

          <IconButton
            variant="icon"
            alignItems="center"
            icon={<AiOutlineMore height="24px" width="24px" />}
            onClick={() => setIsSubMenuVisible(true)}
            aria-label="change theme"
            ml="4px"
            borderRadius="0.5rem"
            display={{ base: "flex", lg: "none" }}
            bg="backgroundCard"
            minW="32px"
            h="32px"
          />

          <SubHeader
            shouldDisplayMenu={isSubMenuVisible}
            onChangeDisplayMenu={(isVisible) => setIsSubMenuVisible(isVisible)}
          />
        </Flex>
      )}
    </Flex>
  );
};

export default Header;
